/**
 * แดชบอร์ด แบบ A (เดิม, /dash) / แบบ B (ใหม่, /dash-b): which one a person last chose is remembered per person in this
 * browser (localStorage, every access in try/catch — private windows throw), and the rail's dashboard button opens it.
 * Deep links always win: /dash?tab=… and /dash-b?… open what they say; only a plain /dash follows the choice.
 */
import type { Router } from 'vue-router'
import { useSession } from '../stores/session'

export type Variant = 'a' | 'b'
const KEY = 'cf-chat.dash-variant'

function key(staffId: string): string {
  return `${KEY}.${staffId}`
}

export function rememberedVariant(staffId: string | null | undefined, store: Pick<Storage, 'getItem'> | null = safeStorage()): Variant | null {
  if (!staffId || !store) return null
  try {
    const v = store.getItem(key(staffId))
    return v === 'a' || v === 'b' ? v : null
  } catch {
    return null
  }
}

export function rememberVariant(staffId: string | null | undefined, v: Variant, store: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  if (!staffId || !store) return
  try {
    store.setItem(key(staffId), v)
  } catch {
    /* private window / storage full: the switch still works for this visit */
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/** The filters both dashboards share, carried over when switching (tabs differ, so the tab is not). */
export const SHARED_QUERY = ['range', 'offset', 'channel', 'from', 'to'] as const

export function sharedQuery(q: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const k of SHARED_QUERY) {
    const v = q[k]
    if (v !== undefined && v !== null && v !== '') out[k] = String(v)
  }
  return out
}

/** Where a plain /dash goes: B when the signed-in owner/manager chose B last time, else stay. */
export function variantTarget(query: Record<string, unknown>, role: string | null | undefined, remembered: Variant | null): 'dash-b' | null {
  if (role !== 'owner' && role !== 'head') return null
  if (query.tab !== undefined || query.view !== undefined || query.staff_id !== undefined) return null
  return remembered === 'b' ? 'dash-b' : null
}

export function installDashVariant(router: Router): void {
  router.beforeEach((to, from) => {
    // already on แบบ A (period / channel / ad changes): stay — แบบ B may have been chosen in another browser tab
    if (to.name !== 'dash' || from.name === 'dash') return true
    let me: { id: string; role: string } | null = null
    try {
      me = useSession().me
    } catch {
      return true // no store yet (tests without Pinia)
    }
    const target = variantTarget(to.query, me?.role, rememberedVariant(me?.id))
    return target ? { name: target, query: sharedQuery(to.query) } : true
  })
}
